import { uuidv7 } from "uuidv7";
import { addDays, differenceInCalendarDays, isValid, parseISO } from "date-fns";
import { isDateOnlyBefore, toIsoDate } from "@common/helpers/date";
import {
    deriveDueDateFromTermSelection,
    deriveTermSelectionFromDueDate,
} from "@common/helpers/invoice-terms.helper";

export {
    buildDefaultEmailBody,
    buildDefaultEmailSubject,
} from "@common/helpers/invoice-email-defaults.helper";
import { lineRowFromSnapshot } from "./hydrateNativeInvoiceFormFromSnapshot";
import {
    NativeInvoiceCreatePrefillLine,
    NativeInvoiceCustomer,
    NativeInvoiceFormState,
    NativeInvoiceLineRow,
} from "./nativeInvoiceFormTypes";

// Matches a simple single-line email address. Used to drop malformed Cc/Bcc
// tokens before they reach the email snapshot: the server validates the arrays
// with z.string().email(), so one bad entry would otherwise 400 the whole
// draft autosave (not just Send) with no field-level feedback. The final label
// disallows dots so a trailing-dot address (e.g. "user@example.com.") is
// rejected client-side while multi-level domains ("user@a.co.uk") still match.
export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@.]+$/;

export const SCHEDULED_SEND_IN_PAST_MESSAGE =
    "Pick a schedule time in the future to schedule send";

const MAX_EMAIL_RECIPIENTS = 50;

export function nonEmptyIdOrUndefined(
    id: string | undefined,
): string | undefined {
    if (id === undefined || id === "") {
        return undefined;
    }
    return id;
}

// Normalizes empty strings (and null/undefined) to null so a cleared field
// never prints on the invoice.
export function nonEmptyStringOrNull(
    value: string | null | undefined,
): string | null {
    if (value == null || value === "") {
        return null;
    }
    return value;
}

// Printed To headline. Display name (counterparty.name) wins so the PDF
// matches that counterparty field. Company is separate: clearing it must not
// drop the To block while a display name is still present. Company is only
// the fallback when there is no display name.
export function invoiceToPartyBusinessName({
    displayName,
    company,
}: {
    displayName?: string | null;
    company?: string | null;
}): string | null {
    return nonEmptyStringOrNull(displayName) ?? nonEmptyStringOrNull(company);
}

// Returns the first non-empty string from the given candidates, or "" when
// none qualify. Used to prefer a primary value (e.g. an entity owner's email)
// while treating empty strings — not just null/undefined — as "missing" so they
// fall through to a fallback.
export function firstNonEmptyString(
    ...values: Array<string | null | undefined>
): string {
    for (const value of values) {
        if (value) {
            return value;
        }
    }
    return "";
}

// Applies a contact-field patch to the selected customer. When no customer is
// selected yet, the patch creates a placeholder customer (empty id) so details
// typed before a counterparty is picked aren't silently discarded. Shared by
// the Details To fields and the send step's Recipient fields.
export function applyCustomerPatch({
    prev,
    patch,
}: {
    prev: NativeInvoiceFormState;
    patch: Partial<NativeInvoiceCustomer>;
}): NativeInvoiceFormState {
    const nextCustomer = prev.customer
        ? { ...prev.customer, ...patch }
        : {
              id: "",
              name: "",
              email: null,
              phoneNumber: null,
              ...patch,
          };

    const nextState: NativeInvoiceFormState = {
        ...prev,
        customer: nextCustomer,
    };
    if (patch.email === undefined) {
        return nextState;
    }
    return {
        ...nextState,
        emailTo: withCustomerEmailInTo({
            emailTo: prev.emailTo,
            previousCustomerEmail: prev.customer?.email,
            nextCustomerEmail: nextCustomer.email,
        }),
    };
}

// Parses a free-text Cc/Bcc field into a deduped list of syntactically valid
// email addresses, excluding any already-used addresses (e.g. the primary
// recipient) so no address is duplicated across to/cc/bcc. Limits results to
// 50 addresses to match the server's max(50) validation.
export function parseRecipientList(
    value: string,
    exclude: string[] = [],
): string[] {
    const taken = new Set(
        exclude.map((email) => email.trim().toLowerCase()).filter(Boolean),
    );
    const result: string[] = [];
    for (const rawEntry of value.split(/[,;\n]/)) {
        const entry = rawEntry.trim();
        const key = entry.toLowerCase();
        if (!EMAIL_PATTERN.test(entry) || taken.has(key)) {
            continue;
        }
        taken.add(key);
        result.push(entry);
    }
    return result.slice(0, MAX_EMAIL_RECIPIENTS);
}

// Swaps the customer's default CC/BCC (counterparty.cc / .bcc) into an email
// recipient field when the customer changes: the previous customer's defaults
// are dropped, the new ones are added, and addresses the user typed by hand
// are kept. Returns `current` untouched when the defaults did not change so a
// no-op reselect never reorders the user's list. Manual entries win over new
// defaults when the combined list would exceed the recipient limit.
export function withCustomerDefaultRecipients({
    current,
    previousDefaults,
    nextDefaults,
}: {
    current: string;
    previousDefaults?: string | null;
    nextDefaults?: string | null;
}): string {
    const previousDefaultEmails = parseRecipientList(previousDefaults ?? "");
    const nextDefaultEmails = parseRecipientList(nextDefaults ?? "");
    if (
        previousDefaultEmails.join(",").toLowerCase() ===
        nextDefaultEmails.join(",").toLowerCase()
    ) {
        return current;
    }
    const manualEmails = parseRecipientList(current, [
        ...previousDefaultEmails,
        ...nextDefaultEmails,
    ]);
    const keptDefaultEmails = nextDefaultEmails.slice(
        0,
        MAX_EMAIL_RECIPIENTS - manualEmails.length,
    );
    return [...keptDefaultEmails, ...manualEmails].join(", ");
}

// True when `email` is one of the addresses carried in from the invoice
// details (reply-to, or the selected customer's CC/BCC). Comparison is
// case-insensitive so a manually typed twin of a detail address stays locked.
export function isDetailSourcedRecipient(
    email: string,
    source: string | null | undefined,
): boolean {
    const key = email.trim().toLowerCase();
    return parseRecipientList(source ?? "").some(
        (locked) => locked.toLowerCase() === key,
    );
}

// Drops addresses the user removed, but puts back any detail-sourced address
// that was in `current`. Addresses that were never in `current` are not
// resurrected.
export function retainLockedRecipients({
    current,
    next,
    lockedSource,
}: {
    current: string;
    next: string;
    lockedSource?: string | null;
}): string {
    const currentEmails = parseRecipientList(current);
    const nextEmails = parseRecipientList(next);
    const nextKeys = new Set(nextEmails.map((email) => email.toLowerCase()));
    const kept = currentEmails.filter(
        (email) =>
            nextKeys.has(email.toLowerCase()) ||
            isDetailSourcedRecipient(email, lockedSource),
    );
    const keptKeys = new Set(kept.map((email) => email.toLowerCase()));
    const added = nextEmails.filter(
        (email) => !keptKeys.has(email.toLowerCase()),
    );
    return [...kept, ...added].join(", ");
}

// Puts the selected customer's email at the front of To when the customer
// (or their email) changes, without wiping extra recipients the user added.
// Passing a blank next email only drops the previous customer address.
export function withCustomerEmailInTo({
    emailTo,
    previousCustomerEmail,
    nextCustomerEmail,
}: {
    emailTo: string;
    previousCustomerEmail?: string | null;
    nextCustomerEmail?: string | null;
}): string {
    const previous = previousCustomerEmail?.trim() ?? "";
    const next = nextCustomerEmail?.trim() ?? "";
    const extraEmails = parseRecipientList(emailTo, previous ? [previous] : []);
    if (!next || !EMAIL_PATTERN.test(next)) {
        return extraEmails.join(", ");
    }
    return [
        next,
        ...extraEmails.filter(
            (email) => email.toLowerCase() !== next.toLowerCase(),
        ),
    ].join(", ");
}

export function defaultLine(): NativeInvoiceLineRow {
    return {
        id: uuidv7(),
        catalogItemId: null,
        catalogItemPriceId: null,
        productOrService: "",
        description: "",
        quantity: 1,
        unitAmount: 0,
        classIds: [],
        accrualAccountId: null,
        receivablesAccountId: null,
        tax: 0,
        discount: 0,
        deposit: 0,
        billableExpenseTransactionId: null,
    };
}

// Builds a form line row from an ad-hoc create-time prefill line (name +
// amount only, no catalog item). Reuses the same snapshot line-row builder
// that hydrates an existing invoice's lines (lineRowFromSnapshot) by wrapping
// the prefill line in a minimal synthetic snapshot-line shape, instead of
// duplicating its quantity/unit-amount/adjustment logic. catalogItemId /
// catalogItemPriceId stay null — the same shape a manually-typed line
// produces, so it saves through the normal draft payload without tripping the
// catalog-reference validation (which only runs when a line carries a
// catalogItemId/PriceId; see
// NativeInvoiceValidationInternalService#assertCatalogItemsValid).
export function prefillLineToRow(
    line: NativeInvoiceCreatePrefillLine,
): NativeInvoiceLineRow {
    return lineRowFromSnapshot({
        line: {
            catalogItemId: null,
            catalogItemPriceId: null,
            productOrService: line.productOrService,
            description: null,
            quantity: 1,
            unitAmount: line.amount,
            amount: line.amount,
            discount: 0,
            tax: 0,
            deposit: 0,
            discountMode: null,
            taxMode: null,
            depositMode: null,
            billableExpenseTransactionId:
                line.billableExpenseTransactionId ?? null,
        },
        generateId: uuidv7,
        codingByLineId: undefined,
    });
}

// The server rejects a due date that precedes the invoice date
// (`nativeInvoiceRenderSnapshotInputSchema`). Single source of truth for both
// the field-level message and the disabled state of the actions that persist
// the draft — the picker's `minDate` only constrains calendar clicks, typed
// input reaches the form state unclamped.
export function getNativeInvoiceDueDateError(
    state: Pick<NativeInvoiceFormState, "dueDate" | "invoiceDate">,
): string | undefined {
    if (!state.dueDate || !state.invoiceDate) {
        return undefined;
    }
    return isDateOnlyBefore(state.dueDate, state.invoiceDate)
        ? "Due date must be on or after the invoice date"
        : undefined;
}

export type ScheduledSendStatus = "valid" | "missing" | "past";

// Classifies the schedule-time requirement for a scheduled send so callers can
// tell a not-yet-picked time apart from one that has already elapsed. Past
// dates stay selectable in the picker, and a once-valid time can lapse while
// the drawer sits open, so both states are real. A 60s tolerance absorbs the
// gap between render and click. Scheduling turned off is always "valid" —
// there is nothing to set.
export function getScheduledSendStatus({
    scheduleEnabled,
    scheduledSendAt,
    now,
}: {
    scheduleEnabled: boolean;
    scheduledSendAt: string | null | undefined;
    now: number;
}): ScheduledSendStatus {
    if (!scheduleEnabled) {
        return "valid";
    }
    // A date-only value (no "T" part) means the user hasn't picked a time yet —
    // the time select never preselects one.
    if (!scheduledSendAt || !scheduledSendAt.includes("T")) {
        return "missing";
    }
    const parsed = parseISO(scheduledSendAt);
    if (!isValid(parsed)) {
        return "missing";
    }
    const SCHEDULE_PAST_TOLERANCE_MS = 60_000;
    return parsed.getTime() >= now - SCHEDULE_PAST_TOLERANCE_MS
        ? "valid"
        : "past";
}

// Names the first unmet requirement blocking Send on the send step. This is the
// single source of truth for the field-level Send gates: useNativeInvoiceHandlers
// derives `sendDisabled` from whether this returns a reason (adding only the
// transient pending/saving states on top), so the tooltip text can't drift out
// of sync with the disabled state. Returns undefined once every field-level
// requirement is satisfied, so a Send disabled only by a transient state shows
// no (empty) tooltip.
export function getSendDisabledTooltip({
    hasDueDateError,
    hasRecipient,
    hasSubject,
    scheduledSendStatus,
}: {
    hasDueDateError: boolean;
    hasRecipient: boolean;
    hasSubject: boolean;
    scheduledSendStatus: ScheduledSendStatus;
}): string | undefined {
    if (hasDueDateError) {
        return "Due date must be on or after the invoice date";
    }
    if (!hasRecipient) {
        return "Add a To email to send this invoice";
    }
    if (!hasSubject) {
        return "Add an email subject to send this invoice";
    }
    if (scheduledSendStatus === "missing") {
        return "Set a schedule time to schedule send";
    }
    if (scheduledSendStatus === "past") {
        return SCHEDULED_SEND_IN_PAST_MESSAGE;
    }
    return undefined;
}

export function withDueDateOverride(
    state: NativeInvoiceFormState,
    dueDate: string,
): NativeInvoiceFormState {
    const derivedTerms = deriveTermSelectionFromDueDate({
        invoiceDate: state.invoiceDate || null,
        dueDate: dueDate || null,
    });
    if (derivedTerms == null) {
        return { ...state, dueDate };
    }
    return {
        ...state,
        dueDate,
        termType: derivedTerms.termType,
        customTerm: derivedTerms.customTerm,
    };
}

// Moving the invoice date forward past the current due date leaves an invalid
// state (due < invoice, rejected server-side by
// nativeInvoiceRenderSnapshotInputSchema). When that happens, auto-push the
// due date out to keep it valid:
// - a terms policy is selected (`termType`) → re-derive the due date from
//   that same policy applied to the new invoice date (mirrors
//   NativeInvoiceTermsField's own recompute-on-change behavior);
// - no policy selected → preserve the previous invoice-date → due-date day
//   gap, shifting the due date by the same number of calendar days.
// Never pulls the due date backward: if it's still on/after the new invoice
// date, only `invoiceDate` changes.
export function withInvoiceDateOverride(
    state: NativeInvoiceFormState,
    invoiceDate: string,
): NativeInvoiceFormState {
    if (!invoiceDate || !state.dueDate) {
        return { ...state, invoiceDate };
    }

    if (!isDateOnlyBefore(state.dueDate, invoiceDate)) {
        return { ...state, invoiceDate };
    }

    if (state.termType != null) {
        const derivedDueDate = deriveDueDateFromTermSelection({
            invoiceDate,
            termType: state.termType,
            customTerm: state.customTerm,
        });
        // A day-of-month custom term (e.g. "due by the 10th of the current
        // month") can resolve to a day that still precedes the new invoice
        // date; accepting it would re-enter the same invalid state we're
        // correcting. Only take the derived date when it keeps due >= invoice,
        // otherwise fall through to the gap-preserving fallback below.
        if (
            derivedDueDate != null &&
            !isDateOnlyBefore(derivedDueDate, invoiceDate)
        ) {
            return { ...state, invoiceDate, dueDate: derivedDueDate };
        }
    }

    const previousInvoiceDate = parseISO(state.invoiceDate);
    const previousDueDate = parseISO(state.dueDate);
    const nextInvoiceDate = parseISO(invoiceDate);
    if (
        !isValid(previousInvoiceDate) ||
        !isValid(previousDueDate) ||
        !isValid(nextInvoiceDate)
    ) {
        return { ...state, invoiceDate };
    }

    const dayGap = differenceInCalendarDays(
        previousDueDate,
        previousInvoiceDate,
    );
    return {
        ...state,
        invoiceDate,
        dueDate: toIsoDate(addDays(nextInvoiceDate, Math.max(dayGap, 0))),
    };
}
